import { useTran } from "@/hooks";
import PillarSummaryChart from "./charts/PillarSummaryChart";

const ESGPillarSummary = ({ pillarCounts }) => {
  const { t } = useTran();
  const pillarColors = {
    Environment: "#3b82f6",
    Social: "#8b5cf6",
    Governance: "#f59e0b",
  };

  const chartData = Object.keys(pillarCounts).map((pillar) => ({
    pillarKey: pillar,
    pillarLabel: t(pillar),
    count: pillarCounts[pillar].total,
    completed: pillarCounts[pillar].completed,
    color: pillarColors[pillar] || "#3b82f6",
  }));

  const totals = Object.values(pillarCounts).reduce(
    (sum, pillar) => ({
      total: sum.total + pillar.total,
      completed: sum.completed + pillar.completed,
    }),
    { total: 0, completed: 0 },
  );

  const completionRate = totals.total
    ? Math.round((totals.completed / totals.total) * 100)
    : 0;

  return (
    <div className="esg-page-break esg-avoid-break bg-white p-12">
      <h2 className="mb-8 text-3xl font-bold text-slate-900">
        {t("Summary of Disclosures by Pillar")}
      </h2>

      <div className="mb-10">
        <table className="esg-summary-table w-full">
          <thead>
            <tr>
              <th className="w-[30%] text-left">{t("Pillar")}</th>
              <th className="w-[20%] text-center">{t("Total Disclosures")}</th>
              <th className="w-[20%] text-center">{t("Completed")}</th>
              <th className="w-[30%] text-center">{t("Completion Rate")}</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(pillarCounts).map((pillar) => {
              const pillarData = pillarCounts[pillar];
              const color = pillarColors[pillar] || "#3b82f6";
              return (
                <tr key={pillar} className="esg-pillar-row">
                  <td className="font-semibold text-slate-900">
                    <div className="flex items-center gap-2">
                      <div
                        className="h-3 w-3 rounded-full"
                        style={{ backgroundColor: color }}
                      />
                      {t(pillar)}
                    </div>
                  </td>
                  <td className="text-center font-semibold text-slate-900">
                    {pillarData.total}
                  </td>
                  <td className="text-center text-slate-900">
                    {pillarData.completed}
                  </td>
                  <td className="text-center">
                    <div className="flex items-center justify-center gap-2">
                      <div className="relative h-2 w-24 rounded-full bg-slate-200">
                        <div
                          className="absolute left-0 top-0 h-2 rounded-full"
                          style={{
                            width: `${pillarData.percentage}%`,
                            backgroundColor: color,
                          }}
                        />
                      </div>
                      <span className="text-sm font-semibold text-slate-700">
                        {pillarData.percentage}%
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
            <tr className="border-t-2 border-slate-300">
              <td className="font-bold text-slate-900">{t("Total")}</td>
              <td className="text-center font-bold text-slate-900">
                {totals.total}
              </td>
              <td className="text-center font-bold text-slate-900">
                {totals.completed}
              </td>
              <td className="text-center">
                <span className="text-sm font-bold text-emerald-600">
                  {completionRate}%
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mt-10">
        <h3 className="esg-chart-title text-center text-lg font-semibold text-slate-700 mb-4">
          {t("Number of Disclosures by Pillar")}
        </h3>
        <PillarSummaryChart data={chartData} />
      </div>

      <div className="mt-8 rounded-lg border border-slate-200 bg-slate-50 p-4">
        <p className="text-xs text-slate-600 leading-relaxed">
          <strong>{t("Note")}:</strong>{" "}
          {t(
            "The summary above provides an overview of disclosure distribution across the three ESG pillars. Completion rates indicate the percentage of disclosures that have been filled with data and reviewed.",
          )}
        </p>
      </div>
    </div>
  );
};

export default ESGPillarSummary;
